import { useEffect } from 'react';
import { useToastStore, type Toast as ToastData } from '../stores/toastStore';

const AUTO_DISMISS_MS = 4000;

/**
 * Single toast chip. Auto-dismisses itself after 4s via a mount-time timer.
 * Clicking the dismiss button removes it immediately.
 */
function ToastItem({ toast }: { toast: ToastData }) {
  const dismiss = useToastStore((s) => s.dismissToast);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      dismiss(toast.id);
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [toast.id, dismiss]);

  const palette =
    toast.kind === 'success'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
      : toast.kind === 'error'
        ? 'bg-red-50 border-red-200 text-red-900'
        : 'bg-slate-50 border-slate-200 text-slate-900';

  return (
    <div
      role="status"
      data-testid={`toast-${toast.kind}`}
      className={`pointer-events-auto flex items-start gap-3 rounded border px-3 py-2 shadow-sm text-sm ${palette}`}
    >
      <span className="flex-1" data-testid="toast-message">
        {toast.message}
      </span>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss notification"
        data-testid="toast-dismiss"
        className="text-current/70 hover:text-current font-bold leading-none"
      >
        ×
      </button>
    </div>
  );
}

/**
 * Mount once near the root of the admin layout. Renders all active toasts
 * in a fixed bottom-right stack. Pointer-events-none on the viewport so
 * the empty space doesn't block clicks to the app behind it.
 */
export default function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80 max-w-[90vw]"
      data-testid="toast-viewport"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
